// Part B precedence graph and order (docs/specs/2026-09-16-tycoon-composition.md, Part B "Order"). Static proxies,
// their 64 px unit-screen grid, static edges and Kahn ranks are cached per graph key; per frame, dynamic fragments
// get edges from grid candidates and a Kahn sort over visible nodes yields the submission order. Kahn priority is
// rotated anchor x+y, then height, then stable id. Residual cycles are broken per Tarjan SCC: geometric/decal edges
// before required (crossing, bed, support) ones, an edge touching scenery first within each, then the least projected
// overlap, then the smallest stable id pair.
//
// Dynamic fragments with identical proxies and pair-rule inputs are indistinguishable to every rule, so they share
// one graph node; its members are emitted consecutively in stable-id order. This keeps coincident consists (the
// dense synthetic snapshot stacks 1,200 identical trains) linear instead of quadratic.
import type { Rotation } from './projection.js';
import {
  boundsOverlap, compile, decide, EPSILON, GRID_CELL_PX, MIN_OVERLAP_AREA, overlapArea, REASON_CODE,
  type Bounds, type CrossingStrata, type EdgeReason, type Fragment, type Node, type PrecedenceEdge, type SortResult,
} from './precedence.js';

/** Growable edge columns; `from`/`to` are node indices. */
class EdgeColumns {
  from: number[] = []; to: number[] = []; reason: number[] = []; crossing: (string | null)[] = []; area: number[] = [];
  get length(): number { return this.from.length; }
  /** Decides a candidate pair and records the edge when the proxies overlap on screen. */
  add(i: number, a: Node, j: number, b: Node, crossings: ReadonlyMap<string, CrossingStrata>): void {
    const decision = decide(a, b, crossings);
    if (!decision) return;
    const area = overlapArea(a, b);
    if (area <= MIN_OVERLAP_AREA) return;
    this.from.push(decision.before === 0 ? i : j); this.to.push(decision.before === 0 ? j : i);
    this.reason.push(REASON_CODE.indexOf(decision.reason)); this.crossing.push(decision.crossingId); this.area.push(area);
  }
}

/** Uniform unit-screen grid over fragment bounds. */
class Grid {
  private readonly cells = new Map<number, number[]>();
  constructor(bounds: readonly Bounds[], private readonly cell = GRID_CELL_PX) {
    bounds.forEach((box, index) => this.each(box, (key) => { const list = this.cells.get(key); if (list) list.push(index); else this.cells.set(key, [index]); }));
  }
  private each(box: Bounds, visit: (key: number) => void): void {
    const x0 = Math.floor(box[0] / this.cell), x1 = Math.floor(box[2] / this.cell), y0 = Math.floor(box[1] / this.cell), y1 = Math.floor(box[3] / this.cell);
    // Keys pack signed 21-bit cell coordinates; network unit-screen extents are far inside that range.
    for (let x = x0; x <= x1; x += 1) for (let y = y0; y <= y1; y += 1) visit((x + 1_048_576) * 2_097_152 + (y + 1_048_576));
  }
  query(box: Bounds, visit: (index: number) => void, seen: Int32Array, stamp: number): void {
    this.each(box, (key) => { const list = this.cells.get(key); if (list) for (const index of list) if (seen[index] !== stamp) { seen[index] = stamp; visit(index); } });
  }
}

/** CSR adjacency. */
interface Csr { start: Int32Array; target: Int32Array; reason: Uint8Array; area: Float64Array; crossing: readonly (string | null)[] }
const toCsr = (count: number, edges: EdgeColumns): Csr & { order: Int32Array } => {
  const start = new Int32Array(count + 1);
  for (const from of edges.from) start[from + 1]! += 1;
  for (let i = 0; i < count; i += 1) start[i + 1]! += start[i]!;
  const fill = start.slice(0, count), n = edges.length, target = new Int32Array(n), reason = new Uint8Array(n), area = new Float64Array(n), crossing: (string | null)[] = new Array(n), order = new Int32Array(n);
  for (let e = 0; e < n; e += 1) { const slot = fill[edges.from[e]!]!++; target[slot] = edges.to[e]!; reason[slot] = edges.reason[e]!; area[slot] = edges.area[e]!; crossing[slot] = edges.crossing[e]!; order[e] = slot; }
  return { start, target, reason, area, crossing, order };
};

/** Kahn priority comparison: rotated anchor x+y, then height, then stable id (code-unit order). */
const precedes = (a: Node, b: Node): boolean =>
  a.depth !== b.depth ? a.depth < b.depth : a.fragment.height !== b.fragment.height ? a.fragment.height < b.fragment.height : a.fragment.id < b.fragment.id;

export interface StaticGraph {
  readonly rotation: Rotation; readonly key: string; readonly size: number; readonly ids: readonly string[];
  readonly nodes: readonly Node[]; readonly index: ReadonlyMap<string, number>; readonly grid: Grid;
  readonly csr: Csr; readonly rank: Float64Array; readonly sorted: Int32Array;
  /** Static CSR slots removed when the graph was built: static-only cycles are broken once, not per frame. */
  readonly brokenStatic: Uint8Array;
  readonly crossings: ReadonlyMap<string, CrossingStrata>; readonly candidatePairs: number; readonly edgeCount: number;
}

export function buildStaticGraph(fragments: readonly Fragment[], options: { rotation: Rotation; crossings?: readonly CrossingStrata[]; key?: string }): StaticGraph {
  const index = new Map<string, number>();
  fragments.forEach((fragment, i) => { if (index.has(fragment.id)) throw new Error(`duplicate fragment id ${fragment.id}`); index.set(fragment.id, i); });
  const nodes = fragments.map((fragment) => compile(fragment, options.rotation)), crossings = new Map((options.crossings ?? []).map((item) => [item.id, item]));
  const grid = new Grid(nodes.map((node) => node.fragment.bounds)), seen = new Int32Array(nodes.length).fill(-1), edges = new EdgeColumns();
  let candidatePairs = 0;
  nodes.forEach((node, i) => grid.query(node.fragment.bounds, (j) => {
    if (j <= i || !boundsOverlap(node.fragment.bounds, nodes[j]!.fragment.bounds)) return;
    candidatePairs += 1;
    edges.add(i, node, j, nodes[j]!, crossings);
  }, seen, i));
  const sorted = Int32Array.from(nodes.map((_, i) => i).sort((a, b) => (precedes(nodes[a]!, nodes[b]!) ? -1 : precedes(nodes[b]!, nodes[a]!) ? 1 : 0)));
  const rank = new Float64Array(nodes.length);
  sorted.forEach((node, position) => { rank[node] = position; });
  const csr = toCsr(nodes.length, edges), brokenStatic = breakStaticCycles(nodes, csr);
  return { rotation: options.rotation, key: options.key ?? '', size: nodes.length, ids: nodes.map((node) => node.fragment.id), nodes, index, grid, csr, rank, sorted, brokenStatic, crossings, candidatePairs, edgeCount: edges.length };
}

/**
 * Cycle-breaking preference: geometric/decal edges first, required (crossing, bed, support) edges only when an SCC has
 * no other edge; among those, an edge touching scenery first (a cycle through a building is the building's to lose, so
 * hiding buildings leaves the network unbroken); then least projected overlap; then the smallest stable id pair.
 */
type BreakKey = [number, number, string, string];
const REQUIRED_REASONS = new Set([REASON_CODE.indexOf('crossing'), REASON_CODE.indexOf('bed'), REASON_CODE.indexOf('support')]);
const tier = (required: boolean, scenery: boolean): number => (required ? 2 : 0) + (scenery ? 0 : 1);
const breakKey = (reason: number, area: number, from: Fragment, to: Fragment): BreakKey => [tier(REQUIRED_REASONS.has(reason), Boolean(from.scenery || to.scenery)), area, from.id, to.id];
/** Strict total order on break keys (exact area, no tolerance), so the choice never depends on enumeration order. */
const betterBreak = (a: BreakKey, b: BreakKey | undefined): boolean => !b || a[0] < b[0] || (a[0] === b[0] && (a[1] < b[1] || (a[1] === b[1] && (a[2] < b[2] || (a[2] === b[2] && a[3] < b[3])))));
export interface BreakCandidate { required: boolean; scenery?: boolean; area: number; before: string; after: string }
/** The candidate the cycle-breaking preference removes (test and diagnostics entry point). */
export function preferredBreak<T extends BreakCandidate>(candidates: readonly T[]): T | undefined {
  let best: { candidate: T; key: BreakKey } | undefined;
  for (const candidate of candidates) { const key: BreakKey = [tier(candidate.required, Boolean(candidate.scenery)), candidate.area, candidate.before, candidate.after]; if (betterBreak(key, best?.key)) best = { candidate, key }; }
  return best?.candidate;
}

/** Removes, per SCC and until acyclic, one static edge chosen by the cycle-breaking preference. */
function breakStaticCycles(nodes: readonly Node[], csr: Csr): Uint8Array {
  const n = nodes.length, removed = new Uint8Array(csr.target.length);
  for (;;) {
    const alive = new Uint8Array(n).fill(1), adjacency: number[][] = Array.from({ length: n }, () => []), slots: number[][] = Array.from({ length: n }, () => []);
    for (let i = 0; i < n; i += 1) for (let e = csr.start[i]!; e < csr.start[i + 1]!; e += 1) if (!removed[e]) { adjacency[i]!.push(csr.target[e]!); slots[i]!.push(e); }
    const components = stronglyConnected(n, alive, adjacency);
    if (!components.length) return removed;
    for (const component of components) {
      const inside = new Set(component);
      let best: { slot: number; key: BreakKey } | undefined;
      for (const from of component) slots[from]!.forEach((slot, k) => {
        const to = adjacency[from]![k]!;
        if (!inside.has(to)) return;
        const key = breakKey(csr.reason[slot]!, csr.area[slot]!, nodes[from]!.fragment, nodes[to]!.fragment);
        if (betterBreak(key, best?.key)) best = { slot, key };
      });
      removed[best!.slot] = 1;
    }
  }
}

export interface FrameSortOptions { staticVisible?: (index: number) => boolean; collectEdges?: boolean; collectRequired?: boolean }
/** `requiredEdges` (crossing and bed edges among visible nodes) is filled when `collectRequired` is set. */
export interface FrameSortResult extends SortResult { requiredEdges: PrecedenceEdge[]; stats: { S: number; E: number; C: number; brokenCycles: number } }

class RankHeap {
  private readonly items: number[] = [];
  constructor(private readonly rank: Float64Array) {}
  get size(): number { return this.items.length; }
  push(value: number): void {
    const items = this.items, rank = this.rank; let i = items.push(value) - 1;
    while (i > 0) { const parent = (i - 1) >> 1; if (rank[items[i]!]! >= rank[items[parent]!]!) break; [items[i], items[parent]] = [items[parent]!, items[i]!]; i = parent; }
  }
  pop(): number {
    const items = this.items, rank = this.rank, top = items[0]!, last = items.pop()!;
    if (items.length) {
      items[0] = last; let i = 0;
      for (;;) {
        const l = 2 * i + 1, r = l + 1; let best = i;
        if (l < items.length && rank[items[l]!]! < rank[items[best]!]!) best = l;
        if (r < items.length && rank[items[r]!]! < rank[items[best]!]!) best = r;
        if (best === i) break;
        [items[i], items[best]] = [items[best]!, items[i]!]; i = best;
      }
    }
    return top;
  }
}

/** Tarjan SCCs over `alive` nodes of an adjacency-list graph; returns components with more than one node. */
function stronglyConnected(count: number, alive: Uint8Array, out: readonly (readonly number[])[]): number[][] {
  const index = new Int32Array(count).fill(-1), low = new Int32Array(count), onStack = new Uint8Array(count), stack: number[] = [], result: number[][] = [];
  let next = 0;
  for (let root = 0; root < count; root += 1) {
    if (!alive[root] || index[root] !== -1) continue;
    const work: Array<[number, number]> = [[root, 0]];
    index[root] = low[root] = next++; stack.push(root); onStack[root] = 1;
    while (work.length) {
      const frame = work[work.length - 1]!, [v, edge] = frame, targets = out[v]!;
      if (edge < targets.length) {
        frame[1] += 1; const w = targets[edge]!;
        if (!alive[w]) continue;
        if (index[w] === -1) { index[w] = low[w] = next++; stack.push(w); onStack[w] = 1; work.push([w, 0]); }
        else if (onStack[w]) low[v] = Math.min(low[v]!, index[w]!);
        continue;
      }
      work.pop();
      if (work.length) { const parent = work[work.length - 1]![0]; low[parent] = Math.min(low[parent]!, low[v]!); }
      if (low[v] === index[v]) {
        const component: number[] = []; let w: number;
        do { w = stack.pop()!; onStack[w] = 0; component.push(w); } while (w !== v);
        if (component.length > 1) result.push(component);
      }
    }
  }
  return result;
}

/** Hash of everything the pair rule and Kahn priority read; equality is then checked exactly. */
const mix = (hash: number, value: number): number => Math.imul(hash ^ ((value * 1048576) | 0), 0x9e3779b1);
function groupHash(fragment: Fragment): number {
  let hash = mix(mix(0x811c9dc5, fragment.proxy.z[0]), fragment.proxy.z[1]);
  const footprint = fragment.proxy.footprint, bounds = fragment.bounds;
  for (let i = 0; i < footprint.length; i += 1) hash = mix(mix(hash, footprint[i]![0]), footprint[i]![1]);
  hash = mix(mix(mix(mix(hash, bounds[0]), bounds[1]), bounds[2]), bounds[3]);
  return mix(mix(mix(hash, fragment.anchor[0]), fragment.anchor[1]), fragment.height);
}
/** Indistinguishable to every pair rule and to the Kahn priority apart from the stable id. */
function interchangeable(a: Fragment, b: Fragment): boolean {
  if (a.role !== b.role || a.corridorId !== b.corridorId || a.overlapRegionId !== b.overlapRegionId || a.height !== b.height) return false;
  if (a.anchor[0] !== b.anchor[0] || a.anchor[1] !== b.anchor[1] || a.proxy.z[0] !== b.proxy.z[0] || a.proxy.z[1] !== b.proxy.z[1]) return false;
  for (let i = 0; i < 4; i += 1) if (a.bounds[i] !== b.bounds[i]) return false;
  const fa = a.proxy.footprint, fb = b.proxy.footprint, ca = a.crossingIds, cb = b.crossingIds;
  if (fa.length !== fb.length || ca.length !== cb.length) return false;
  for (let i = 0; i < fa.length; i += 1) if (fa[i]![0] !== fb[i]![0] || fa[i]![1] !== fb[i]![1]) return false;
  for (let i = 0; i < ca.length; i += 1) if (ca[i] !== cb[i]) return false;
  return true;
}
interface DynamicGroup { rep: Fragment; ids: string[] }
/** Merges interchangeable dynamic fragments (and their pre-grouped members); ids end in stable-id order. */
function groupDynamic(fragments: readonly Fragment[]): DynamicGroup[] {
  const buckets = new Map<number, Fragment[][]>(), groups: Fragment[][] = [];
  for (const fragment of fragments) {
    const hash = groupHash(fragment), bucket = buckets.get(hash);
    let group: Fragment[] | undefined;
    if (bucket) for (let i = 0; i < bucket.length && !group; i += 1) if (interchangeable(bucket[i]![0]!, fragment)) group = bucket[i];
    if (group) { group.push(fragment); continue; }
    const created = [fragment]; groups.push(created);
    if (bucket) bucket.push(created); else buckets.set(hash, [created]);
  }
  return groups.map((group) => {
    const ids = group.length === 1 ? [...(group[0]!.members ?? [group[0]!.id])] : group.flatMap((fragment) => fragment.members ?? [fragment.id]);
    return { rep: group[0]!, ids: ids.length > 1 ? ids.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)) : ids };
  });
}

/**
 * Per-frame order: dynamic groups get edges against visible statics (cached grid) and each other, then a Kahn
 * sort over visible nodes. Static visibility comes from `staticVisible`; layer toggles and culling only hide nodes.
 */
export function sortFrame(graph: StaticGraph, dynamic: readonly Fragment[], options: FrameSortOptions = {}): FrameSortResult {
  // Dynamic ids must not collide with static ids (the view namespaces cars as `car:`); checked per representative.
  const s = graph.size, visibleDynamic = dynamic.filter((fragment) => fragment.visible);
  for (const fragment of visibleDynamic) if (graph.index.has(fragment.id)) throw new Error(`dynamic fragment id ${fragment.id} collides with a static fragment`);
  // Collapse indistinguishable dynamic fragments; members keep stable-id order.
  const groups = groupDynamic(visibleDynamic), members = groups.map((group) => group.ids);
  const reps = groups.map((group) => compile(group.rep, graph.rotation)), g = reps.length, total = s + g;
  const nodes: Node[] = [...graph.nodes, ...reps];
  const alive = new Uint8Array(total);
  let staticCount = 0;
  for (let i = 0; i < s; i += 1) if (graph.nodes[i]!.fragment.visible && (options.staticVisible?.(i) ?? true)) { alive[i] = 1; staticCount += 1; }
  alive.fill(1, s);
  // Dynamic edges: statics through the cached grid, other groups through a per-frame grid.
  const extra = new EdgeColumns(), seen = new Int32Array(s).fill(-1), groupGrid = new Grid(reps.map((node) => node.fragment.bounds)), groupSeen = new Int32Array(g).fill(-1);
  let candidates = 0;
  for (let k = 0; k < g; k += 1) {
    const node = reps[k]!, i = s + k;
    graph.grid.query(node.fragment.bounds, (j) => {
      if (!alive[j] || !boundsOverlap(node.fragment.bounds, graph.nodes[j]!.fragment.bounds)) return;
      candidates += 1; extra.add(j, graph.nodes[j]!, i, node, graph.crossings);
    }, seen, k);
    groupGrid.query(node.fragment.bounds, (m) => {
      if (m <= k || !boundsOverlap(node.fragment.bounds, reps[m]!.fragment.bounds)) return;
      candidates += 1; extra.add(i, node, s + m, reps[m]!, graph.crossings);
    }, groupSeen, k);
  }
  const dyn = toCsr(total, extra), st = graph.csr, stEdges = st.target.length, removed = new Uint8Array(stEdges + extra.length);
  removed.set(graph.brokenStatic);
  // Kahn ranks: statics use cached positions; groups slot in by binary search over the cached static order.
  const rank = new Float64Array(total);
  rank.set(graph.rank);
  const repOrder = reps.map((_, k) => k).sort((a, b) => (precedes(reps[a]!, reps[b]!) ? -1 : precedes(reps[b]!, reps[a]!) ? 1 : 0));
  repOrder.forEach((k, j) => {
    let lo = 0, hi = s;
    while (lo < hi) { const mid = (lo + hi) >> 1; if (precedes(graph.nodes[graph.sorted[mid]!]!, reps[k]!)) lo = mid + 1; else hi = mid; }
    rank[s + k] = lo - 0.5 + j / (g + 1) * 0.5;
  });
  const indegree = new Int32Array(total);
  let edgeCount = 0;
  for (let i = 0; i < s; i += 1) if (alive[i]) for (let e = st.start[i]!; e < st.start[i + 1]!; e += 1) if (!graph.brokenStatic[e] && alive[st.target[e]!]) { indegree[st.target[e]!]! += 1; edgeCount += 1; }
  for (let e = 0; e < dyn.target.length; e += 1) { indegree[dyn.target[e]!]! += 1; edgeCount += 1; }
  const heap = new RankHeap(rank), order: string[] = [], placed = new Uint8Array(total), brokenEdges: PrecedenceEdge[] = [];
  let brokenCycles = 0;
  for (let i = 0; i < total; i += 1) if (alive[i] && indegree[i] === 0) heap.push(i);
  const release = (j: number) => { indegree[j]! -= 1; if (indegree[j] === 0) heap.push(j); };
  const emit = (i: number) => {
    placed[i] = 1;
    if (i < s) order.push(graph.ids[i]!); else for (const member of members[i - s]!) order.push(member);
    if (i < s) for (let e = st.start[i]!; e < st.start[i + 1]!; e += 1) { const j = st.target[e]!; if (alive[j] && !removed[e]) release(j); }
    for (let e = dyn.start[i]!; e < dyn.start[i + 1]!; e += 1) if (!removed[stEdges + e]) release(dyn.target[e]!);
  };
  // Edge views by global id (static CSR slot, or stEdges + dynamic CSR slot).
  const edgeFrom = (edge: number, from: number) => ({ from, to: edge < stEdges ? st.target[edge]! : dyn.target[edge - stEdges]!, reason: edge < stEdges ? st.reason[edge]! : dyn.reason[edge - stEdges]!, area: edge < stEdges ? st.area[edge]! : dyn.area[edge - stEdges]!, crossing: edge < stEdges ? st.crossing[edge]! : dyn.crossing[edge - stEdges]! });
  const expand = (from: number, to: number, reason: number, crossing: string | null, area: number): PrecedenceEdge[] => {
    const ids = (node: number) => node < s ? [graph.ids[node]!] : members[node - s]!;
    return ids(from).flatMap((before) => ids(to).map((after) => ({ before, after, reason: REASON_CODE[reason]! as EdgeReason, crossingId: crossing, overlapArea: area })));
  };
  // Static cycle breaks from graph construction count against every frame in which both ends are visible.
  for (let i = 0; i < s; i += 1) if (alive[i]) for (let e = st.start[i]!; e < st.start[i + 1]!; e += 1) if (graph.brokenStatic[e] && alive[st.target[e]!]) {
    brokenCycles += 1; brokenEdges.push(...expand(i, st.target[e]!, st.reason[e]!, st.crossing[e]!, st.area[e]!));
  }
  const nodeCount = staticCount + g;
  let placedCount = 0;
  while (placedCount < nodeCount) {
    while (heap.size) { emit(heap.pop()); placedCount += 1; }
    if (placedCount >= nodeCount) break;
    // Slow path: residual nodes lie on or behind cycles. Break one edge per SCC, least overlap then id pair.
    const remaining = new Uint8Array(total), adjacency: number[][] = Array.from({ length: total }, () => []), edgeIds: number[][] = Array.from({ length: total }, () => []);
    for (let i = 0; i < total; i += 1) if (alive[i] && !placed[i]) remaining[i] = 1;
    for (let i = 0; i < total; i += 1) {
      if (!remaining[i]) continue;
      if (i < s) for (let e = st.start[i]!; e < st.start[i + 1]!; e += 1) if (!removed[e] && remaining[st.target[e]!]) { adjacency[i]!.push(st.target[e]!); edgeIds[i]!.push(e); }
      for (let e = dyn.start[i]!; e < dyn.start[i + 1]!; e += 1) if (!removed[stEdges + e] && remaining[dyn.target[e]!]) { adjacency[i]!.push(dyn.target[e]!); edgeIds[i]!.push(stEdges + e); }
    }
    const components = stronglyConnected(total, remaining, adjacency);
    if (!components.length) throw new Error('precedence graph stalled without a cycle');
    for (const component of components) {
      const inside = new Set(component);
      let best: { edge: number; from: number; key: BreakKey } | undefined;
      for (const from of component) edgeIds[from]!.forEach((edge, slot) => {
        const to = adjacency[from]![slot]!;
        if (!inside.has(to)) return;
        const view = edgeFrom(edge, from), key = breakKey(view.reason, view.area, nodes[from]!.fragment, nodes[to]!.fragment);
        if (betterBreak(key, best?.key)) best = { edge, from, key };
      });
      const view = edgeFrom(best!.edge, best!.from);
      removed[best!.edge] = 1; edgeCount -= 1; brokenCycles += 1;
      brokenEdges.push(...expand(view.from, view.to, view.reason, view.crossing, view.area));
      release(view.to);
    }
  }
  const edges: PrecedenceEdge[] = [], requiredEdges: PrecedenceEdge[] = [], bed = REASON_CODE.indexOf('bed'), crossingCode = REASON_CODE.indexOf('crossing');
  if (options.collectEdges || options.collectRequired) for (let i = 0; i < total; i += 1) {
    if (!alive[i]) continue;
    const visit = (edge: number) => {
      if (removed[edge]) return;
      const view = edgeFrom(edge, i);
      if (!alive[view.to]) return;
      if (options.collectEdges) edges.push(...expand(i, view.to, view.reason, view.crossing, view.area));
      if (options.collectRequired && REQUIRED_REASONS.has(view.reason)) requiredEdges.push(...expand(i, view.to, view.reason, view.crossing, view.area));
    };
    if (i < s) for (let e = st.start[i]!; e < st.start[i + 1]!; e += 1) visit(e);
    for (let e = dyn.start[i]!; e < dyn.start[i + 1]!; e += 1) visit(stEdges + e);
  }
  return { order, edges, requiredEdges, brokenEdges, stats: { S: staticCount + members.reduce((sum, ids) => sum + ids.length, 0), E: edgeCount, C: graph.candidatePairs + candidates, brokenCycles } };
}

/** One-shot convenience: statics are the non-dynamic fragments. */
export function sortFragments(fragments: readonly Fragment[], options: { rotation: Rotation; crossings?: readonly CrossingStrata[]; collectEdges?: boolean; collectRequired?: boolean }): FrameSortResult {
  const graph = buildStaticGraph(fragments.filter((fragment) => !fragment.dynamic), options);
  return sortFrame(graph, fragments.filter((fragment) => fragment.dynamic), { collectEdges: options.collectEdges, collectRequired: options.collectRequired });
}
